import { useState, useEffect } from 'react';
import constate from 'constate';
import { useLocation } from 'react-router-dom';
import { queryClient } from '../../core/queryClient';
import { useAllFx, isUsableRate } from '../../hooks/fx';
import { fxCode, getBaseCurrency } from '../../core/currency';

// Shown in place of Left to pay / Profit estimate when a needed rate is
// unknown. See FX RULE in hooks/fx.js.
export const FX_RATE_UNAVAILABLE = 'Rate unavailable';

// Mirror of useOrderManagement's convertAmount: `rates` is a map keyed by
// the SOURCE currency (upper-case) whose value converts 1 unit of that
// currency INTO `to`, the currency the rates were fetched for
// (useAllFx(to)). Same currency (or a 1:1 peg such as USDT/USD), or a row
// with no currency, is ×1 — so an unseeded/flag-off GBP deal is
// byte-identical. A DIFFERENT currency with no rate is NaN, never ×1: the
// callers turn NaN into FX_RATE_UNAVAILABLE.
const convertAmount = (rates, amount, from = 'gbp', to) => {
  const direct = rates[from?.toUpperCase()];
  if (isUsableRate(direct)) return Number(amount) * direct;
  if (!from || !to || fxCode(from) === fxCode(to)) return Number(amount) * 1;
  const peg = rates[fxCode(from).toUpperCase()];
  if (isUsableRate(peg)) return Number(amount) * peg;
  return Number(amount) ? NaN : 0;
};

// Aggregate the selling price across every main slot (primary +
// additional). Order of resolution per slot:
//   1. The slot's last_quote.price (rep selected a quote)
//   2. Its linked listing's price (from listings[i])
//   3. order.total_amount (only as a last-ditch fallback for single-main)
// All amounts are converted INTO the deal currency (`dealCurrency`) via
// `ratesToDeal` (from useAllFx(dealCurrency)). A slot's selling amount is in
// its quote's own currency (quote-sourced) or in the deal currency
// (listing-sourced — listing.price carries no currency column and is
// denominated in the order/deal currency, matching useOrderManagement.toPay).
const sumMainSellingPrice = (order, mainRequests, listings, quotes, ratesToDeal, dealCurrency) => {
  let total = 0;
  let anyResolved = false;
  for (let i = 0; i < mainRequests.length; i++) {
    const req = mainRequests[i];
    if (!req) continue;
    const slotQuote = quotes?.[req.id];
    const fromQuote = slotQuote?.price;
    const fromListing = listings?.[i]?.price;
    if (fromQuote != null) {
      total += convertAmount(ratesToDeal, fromQuote, slotQuote?.currency, dealCurrency);
      anyResolved = true;
    } else if (fromListing != null) {
      total += convertAmount(ratesToDeal, fromListing, dealCurrency, dealCurrency);
      anyResolved = true;
    }
  }
  if (!anyResolved) {
    // Single-main legacy fallback — use order.total_amount when no
    // per-slot price could be resolved. total_amount is in the deal
    // currency, so this is effectively ×1.
    if (order?.total_amount != null)
      return convertAmount(ratesToDeal, order.total_amount, dealCurrency, dealCurrency);
    return null;
  }
  return total;
};

// Net pay (client-facing / transactional) is returned in the DEAL currency:
// every sell quote (buying side) and every main selling amount is converted
// into `dealCurrency` before combining.
const calculateNetPay = (order, request, mainRequests, listings, pxRequests, quotes, ratesToDeal, dealCurrency) => {
  let totalBuyingPrice = 0;

  if (!pxRequests?.length) {
    return ['N/A'];
  }

  for (const pxRequest of pxRequests) {
    // Fall back to pxRequest.last_quote when the calc context hasn't
    // seeded quotes for this sell request — otherwise Live Calc shows
    // "N/A" the moment you land on a px-order page, even though the
    // backend already has the accepted quote attached to the request.
    const quote = quotes[pxRequest.id] ?? pxRequest.last_quote;

    if (!quote) {
      return 'N/A';
    }

    totalBuyingPrice += convertAmount(ratesToDeal, quote.price, quote.currency, dealCurrency);
  }

  // Multi-main: sum selling price over all mains. mainRequests
  // already includes the primary `request` (caller passes it as the
  // first entry), so this also handles the single-main case
  // identically.
  const sellingPrice = sumMainSellingPrice(order, mainRequests, listings, quotes, ratesToDeal, dealCurrency);
  if (sellingPrice == null) return 'N/A';

  const net = sellingPrice - totalBuyingPrice;
  return Number.isNaN(net) ? FX_RATE_UNAVAILABLE : net;
}

// Profit is the GBP pivot (per the multi-currency convention: gbp_* cost
// columns are GBP, internal net-profit is computed in GBP). The buying side
// is already GBP (quote.product_price.gbp_price / listing.gbp_buying_price);
// the selling side and the sell-quote pricing are converted INTO GBP via
// `ratesToGbp` (from useAllFx('gbp')). `dealCurrency` labels the selling
// amounts that have no per-record currency (listing.price / order.total_amount).
const calculateProfit = (order, request, mainRequests, listings, pxRequests, quotes, ratesToGbp, dealCurrency) => {
  // Aggregate per-main profit (selling − buying). Selling resolves
  // from the slot's quote price or listing price; buying resolves
  // from quote.product_price.gbp_price for order-request-source
  // slots or listing.gbp_buying_price for listing-source slots.
  let orderProfit = 0;
  let buyingPriceMissing = false;
  let resolvedAny = false;
  for (let i = 0; i < mainRequests.length; i++) {
    const req = mainRequests[i];
    if (!req) continue;
    const slotQuote = quotes?.[req.id];
    const slotListing = listings?.[i];
    const sellRaw =
      slotQuote?.price ?? slotListing?.price ?? null;
    // Quote-sourced selling amounts carry their own currency; listing-sourced
    // ones are denominated in the deal currency.
    const sellCurrency =
      slotQuote?.price != null ? slotQuote?.currency : dealCurrency;
    const slotBuying =
      slotQuote?.product_price?.gbp_price ??
      slotListing?.gbp_buying_price ??
      null;
    if (sellRaw == null) return ['N/A'];
    if (slotBuying == null) {
      buyingPriceMissing = true;
      continue;
    }
    // selling → GBP; buying is already GBP.
    orderProfit += convertAmount(ratesToGbp, sellRaw, sellCurrency, 'gbp') - Number(slotBuying);
    resolvedAny = true;
  }

  if (!resolvedAny && !mainRequests.length && order) {
    // Single-main legacy fallback when no mainRequests passed:
    // use order.items[0].listing for buying / order.total_amount for
    // selling — preserves the previous behaviour.
    const listing = order?.items?.[0]?.listing;
    if (!listing?.gbp_buying_price) {
      return ['N/A', 'Input Listing Buying Price'];
    }
    orderProfit =
      convertAmount(ratesToGbp, order?.total_amount, dealCurrency, 'gbp') -
      Number(listing.gbp_buying_price);
  } else if (buyingPriceMissing) {
    return ['N/A', 'Input Listing Buying Price'];
  } else if (!resolvedAny) {
    return ['N/A'];
  }

  if (!pxRequests?.length) {
    return ['N/A'];
  }

  let minSellingProfit = 0;
  let maxSellingProfit = 0;
  for (const pxRequest of pxRequests) {
    const quote = quotes[pxRequest.id] ?? pxRequest.last_quote;

    if (!quote || !quote.pricing) {
      return ['N/A'];
    }

    // min_trade / max_dao and quote.price are all in the sell quote's own
    // currency — convert each (price-relative) difference into GBP.
    minSellingProfit += convertAmount(ratesToGbp, quote.pricing.min_trade - quote.price, quote.currency, 'gbp');
    maxSellingProfit += convertAmount(ratesToGbp, quote.pricing.max_dao - quote.price, quote.currency, 'gbp');
  }

  if ([orderProfit, minSellingProfit, maxSellingProfit].some(Number.isNaN)) {
    return [FX_RATE_UNAVAILABLE];
  }

  return [{
    min: parseInt(orderProfit + minSellingProfit),
    max: parseInt(orderProfit + maxSellingProfit),
  }];
}

export function useRequestCalculation({ useRequestContext, isOrderFlow }) {
  const location = useLocation();
  const {
    order,
    request,
    pxRequests,
    updateQuotes,
    quotes,
    setQuotes,
    // Multi-main: caller's context may also pass additional main
    // requests + listings so the calc aggregates across slots. The
    // primary `request` is folded into mainRequests below so single-
    // main callers (which only provide `request`) keep working
    // identically.
    additionalMainRequests,
    listings,
  } = useRequestContext();

  // Deal/order currency: the currency net pay is expressed in. Resolve from
  // the order, then any quote in scope, then any listing, else 'gbp'. Unseeded
  // / flag-off ⇒ 'gbp'. (Best-effort — this context isn't given editingOrder,
  // which useOrderManagement prefers; for a GBP deal all sources resolve gbp.)
  const dealCurrency = (
    order?.currency ||
    Object.values(quotes || {}).find((q) => q?.currency)?.currency ||
    (listings ?? []).map((l) => l?.currency).find(Boolean) ||
    // Store base — never a hardcoded gbp ('gbp' until seeded, so flag-off
    // output is byte-identical).
    getBaseCurrency()
  ).toLowerCase();
  // Rates INTO the deal currency (for net pay) and INTO GBP (for the profit
  // pivot). Until a table loads it is {}: same-currency amounts stay ×1, a
  // different currency reads FX_RATE_UNAVAILABLE (never a guessed ×1).
  const { rates: ratesToDeal } = useAllFx(dealCurrency);
  const { rates: ratesToGbp } = useAllFx('gbp');

  const [results, setResults] = useState({});

  useEffect(() => {
    if (!/calculation$/.test(location.pathname) &&
      !/calculation\/[0-9]+$/.test(location.pathname) &&
      !isOrderFlow
    ) {
      return;
    }

    const updatedQuotes = Object.keys(quotes || {}).reduce((acc, key) => {
      const cacheQuote = queryClient.getQueryCache().find(['quote', quotes[key].id]);
      if (cacheQuote?.state?.data) {
        acc[key] = cacheQuote.state.data;
      } else {
        acc[key] = quotes[key];
      }

      return acc;
    }, {});

    const mainRequests = [
      request,
      ...(additionalMainRequests ?? []),
    ].filter(Boolean);

    setResults({
      netPay: calculateNetPay(order, request, mainRequests, listings ?? [], pxRequests, updatedQuotes, ratesToDeal, dealCurrency),
      profit: calculateProfit(order, request, mainRequests, listings ?? [], pxRequests, updatedQuotes, ratesToGbp, dealCurrency),
    });
  }, [order, request, additionalMainRequests, listings, pxRequests, quotes, location.pathname, ratesToDeal, ratesToGbp, dealCurrency]);

  const getSelectionForItem = (item) => {
    return {
      selectedQuote: quotes[item.id],
      onQuoteSelected: (quote) => {
        const newQuotes = {
          ...quotes,
          [item.id]: quote,
        };

        updateQuotes(newQuotes);
        setQuotes(newQuotes);
      },
    };
  };

  return {
    getSelectionForItem,
    results,
    request,
    // The currency netPay is denominated in (quote → order → listing → base)
    // — consumers label the Left-to-pay figure with this, never hardcoded gbp.
    dealCurrency,
  };
}

const [RequestCalculationProvider, useRequestCalculationContext] = constate(useRequestCalculation);

export { RequestCalculationProvider, useRequestCalculationContext };
